/**
 * ergo-360-product.js
 * 产品交互展示区域 - 5个身体部位切换
 */

/**
 * 序列帧基类
 * 提供格式检测、canvas 设置、图片加载等通用功能
 */
class FrameSequenceBase {
  constructor(canvas, options) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.baseUrl = options.baseUrl;
    this.frameCount = options.frameCount;

    this.frames = [];
    this.isLoaded = false;
    this.format = null;

    // canvas 绘制尺寸
    this.drawWidth = 0;
    this.drawHeight = 0;
  }

  /**
   * 检测浏览器支持的最佳图片格式
   * 优先级: avif > webp > png
   */
  async detectBestFormat() {
    if (await this.checkFormatSupport('avif')) {
      return 'avif';
    }
    if (await this.checkFormatSupport('webp')) {
      return 'webp';
    }
    return 'png';
  }

  /**
   * 检测浏览器是否支持指定格式
   */
  checkFormatSupport(format) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);

      // 使用 base64 编码的最小测试图片
      if (format === 'avif') {
        img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABpAQ0AIyExAAAAAP+I9JAAAAAAD/2Q==';
      } else if (format === 'webp') {
        img.src = 'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
      } else {
        resolve(true); // PNG 总是支持的
      }
    });
  }

  /**
   * 设置 canvas 尺寸
   * @returns {boolean} 是否成功设置（返回 false 表示尺寸为 0）
   */
  setupCanvas() {
    const rect = this.canvas.getBoundingClientRect();

    // 如果尺寸为 0，说明 canvas 不可见，跳过设置
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    const dpr = window.devicePixelRatio || 1;

    // canvas 内部尺寸（高清适配）
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    // 重新获取 context（设置 canvas.width/height 会重置 context）
    this.ctx = this.canvas.getContext('2d');

    // 缩放上下文以匹配 CSS 尺寸
    this.ctx.scale(dpr, dpr);

    // 绘制尺寸
    this.drawWidth = rect.width;
    this.drawHeight = rect.height;

    return true;
  }

  /**
   * 加载单张图片
   */
  loadImage(url, index) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.frames[index] = img;
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = url;
    });
  }

  /**
   * 绘制指定帧
   */
  drawFrame(index) {
    if (index < 0 || index >= this.frameCount) return;
    const frame = this.frames[index];
    if (!frame) return;

    this.ctx.clearRect(0, 0, this.drawWidth, this.drawHeight);
    this.ctx.drawImage(frame, 0, 0, this.drawWidth, this.drawHeight);
  }

  /**
   * 重置 canvas
   */
  reset() {
    if (this.ctx && this.drawWidth && this.drawHeight) {
      this.ctx.clearRect(0, 0, this.drawWidth, this.drawHeight);
    }
  }
}

/**
 * 序列帧播放器类
 * 用于在 canvas 上播放序列帧动画
 */
class FrameSequencePlayer extends FrameSequenceBase {
  constructor(canvas, options) {
    super(canvas, options);
    this.frameName = options.frameName;
    this.fps = options.fps;

    this.currentFrame = 0;
    this.isPlaying = false;

    this.frameInterval = 1000 / this.fps; // 40ms for 25fps
    this.lastFrameTime = 0;
    this.animationId = null;
  }

  /**
   * 预加载所有序列帧
   */
  async preload() {
    if (this.isLoaded) return;

    // 检测最佳格式
    this.format = await this.detectBestFormat();

    const promises = [];

    for (let i = 0; i < this.frameCount; i++) {
      const frameNum = String(i).padStart(5, '0');
      let url;

      if (this.format === 'avif') {
        url = `${this.baseUrl}avif/${encodeURIComponent(this.frameName)}_${frameNum}.avif`;
      } else if (this.format === 'webp') {
        url = `${this.baseUrl}webp/${encodeURIComponent(this.frameName)}_${frameNum}.webp`;
      } else {
        url = `${this.baseUrl}${encodeURIComponent(this.frameName)}_${frameNum}.png`;
      }

      promises.push(this.loadImage(url, i));
    }

    await Promise.all(promises);
    this.isLoaded = true;

    // 设置 canvas 尺寸
    this.setupCanvas();
  }

  /**
   * 播放一次动画
   */
  async play() {
    // 如果未加载，先预加载
    if (!this.isLoaded) {
      await this.preload();
    }

    // 如果正在播放，先停止
    if (this.isPlaying) {
      this.stop();
    }

    // 重新设置 canvas 尺寸（响应窗口变化）
    this.setupCanvas();

    // 重置状态
    this.currentFrame = 0;
    this.isPlaying = true;
    this.lastFrameTime = performance.now();

    // 添加播放中状态类
    this.canvas.classList.add('is-playing');

    // 开始动画循环
    this.animate();
  }

  /**
   * 动画循环
   */
  animate() {
    if (!this.isPlaying) return;

    const now = performance.now();
    const delta = now - this.lastFrameTime;

    if (delta >= this.frameInterval) {
      this.drawFrame(this.currentFrame);
      this.currentFrame++;
      this.lastFrameTime = now - (delta % this.frameInterval);

      // 播放完成，循环回到第一帧继续播放
      if (this.currentFrame >= this.frameCount) {
        this.currentFrame = 0;
      }
    }

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  /**
   * 停止播放
   */
  stop() {
    this.isPlaying = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * 重置到初始状态（清空 canvas）
   */
  reset() {
    this.stop();
    this.currentFrame = 0;
    super.reset();
    this.canvas.classList.remove('is-playing');
  }
}

/**
 * 序列帧 Scrub 控制器类
 * 用于在 canvas 上根据滑条位置显示对应帧
 */
class FrameSequenceScrubber extends FrameSequenceBase {
  constructor(canvas, options) {
    super(canvas, options);
    this.isLoading = false;
    this.preloadPromise = null;
    this.currentFrameIndex = -1;
  }

  /**
   * 设置 canvas 尺寸（覆盖基类方法以重置帧索引）
   */
  setupCanvas() {
    const success = super.setupCanvas();
    if (success) {
      // 重置当前帧索引，确保下次 showFrameByPercent 会重绘
      this.currentFrameIndex = -1;
    }
    return success;
  }

  /**
   * 预加载所有序列帧
   * 返回 Promise，确保多次调用都能等待加载完成
   */
  async preload() {
    // 已加载完成，直接返回
    if (this.isLoaded) return Promise.resolve();

    // 正在加载中，返回现有的 Promise 让调用者等待
    if (this.isLoading && this.preloadPromise) {
      return this.preloadPromise;
    }

    this.isLoading = true;

    // 创建并存储预加载 Promise
    this.preloadPromise = this._doPreload();
    return this.preloadPromise;
  }

  /**
   * 实际执行预加载的内部方法
   */
  async _doPreload() {
    // 检测最佳格式
    this.format = await this.detectBestFormat();

    const promises = [];

    // 帧命名：frame_0001 到 frame_0165（4位数字）
    for (let i = 1; i <= this.frameCount; i++) {
      const frameNum = String(i).padStart(4, '0');
      const url = `${this.baseUrl}${this.format}/frame_${frameNum}.${this.format}`;
      promises.push(this.loadImage(url, i - 1)); // 存储到 0-based 索引
    }

    await Promise.all(promises);
    this.isLoaded = true;
    this.isLoading = false;

    // 尝试设置 canvas 尺寸（如果 canvas 可见的话）
    this.setupCanvas();
  }

  /**
   * 绘制指定帧（覆盖基类方法以记录当前帧索引）
   */
  drawFrame(index) {
    super.drawFrame(index);
    this.currentFrameIndex = index;
  }

  /**
   * 根据百分比显示对应帧
   * @param {number} percent - 0-100 的百分比值
   */
  showFrameByPercent(percent) {
    if (!this.isLoaded) {
      return;
    }

    // 确保 canvas 尺寸正确
    if (this.drawWidth === 0 || this.drawHeight === 0) {
      const success = this.setupCanvas();
      if (!success) {
        return;
      }
    }

    // 计算帧索引 (0 到 frameCount-1)
    const frameIndex = Math.min(
      Math.floor((percent / 100) * this.frameCount),
      this.frameCount - 1
    );

    // 如果帧没变，不重绘
    if (frameIndex === this.currentFrameIndex) return;

    this.drawFrame(frameIndex);
  }

  /**
   * 重置 canvas
   */
  reset() {
    this.currentFrameIndex = -1;
    super.reset();
  }
}

class Ergo360Product {
  // Quiz 结果到 Tab 状态的映射
  static QUIZ_RESULT_TO_TAB = {
    'neck': 'NECK',
    'spine': 'SPINE',
    'hips': 'HIPS',
    'legs': 'LEGS'
  };

  constructor(container) {
    this.container = container;
    this.currentState = null;
    this.isAnimating = false;
    this.entranceTriggered = false;
    this.animationTimer = null;

    // 移动端检测
    this.isMobile = window.innerWidth < 1024;

    // 缓存 DOM 元素
    this.navBtns = container.querySelectorAll('.ergo-360-product__nav-btn');
    this.backgrounds = container.querySelectorAll('.ergo-360-product__bg');
    this.cards = container.querySelectorAll('.ergo-360-product__card');
    this.hotspots = container.querySelectorAll('.ergo-360-product__hotspot');
    this.lines = container.querySelectorAll('.ergo-360-product__line');
    this.popups = container.querySelectorAll('.ergo-360-product__popup');
    this.mobilePopups = container.querySelectorAll('.ergo-360-product__mobile-popup');

    // 序列帧播放器 Map (state -> FrameSequencePlayer)
    this.frameSequencePlayers = new Map();

    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 1024;
    });

    this.init();
  }

  init() {
    this.bindNavEvents();
    this.bindHotspotEvents();
    this.setInitialState();
    this.initPopups();
    this.initMobilePopups();
    this.initEntranceAnimation();
    this.initQuizResult();
    this.initFrameSequencePlayers();
  }

  /**
   * 绑定导航按钮点击事件
   */
  bindNavEvents() {
    this.navBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const state = btn.dataset.state;
        if (state && state !== this.currentState) {
          this.switchState(state);
        }
      });
    });
  }

  /**
   * 绑定 hotspot hover 事件（仅 PC 端）
   */
  bindHotspotEvents() {
    if (this.isMobile) return;

    this.hotspots.forEach((hotspot) => {
      const video = hotspot.querySelector('video');
      const content = hotspot.querySelector('.ergo-360-product__hotspot-content');
      let expandTimer = null;
      let collapseTimer = null;

      hotspot.addEventListener('mouseenter', () => {
        if (this.isMobile) return;

        // PC 端：hover 时预加载序列帧
        const productName = hotspot
          .querySelector('.ergo-360-product__hotspot-name')
          ?.textContent.toLowerCase()
          .trim();
        if (productName) {
          this.preloadFrameScrubber(productName);
        }

        // 清除可能存在的收起定时器
        if (collapseTimer) {
          clearTimeout(collapseTimer);
          collapseTimer = null;
        }

        // Phase 1: 宽度展开
        if (content) {
          const currentWidth = content.offsetWidth;
          content.style.width = currentWidth + 'px';
          content.offsetHeight; // 强制回流
        }
        hotspot.classList.add('is-expanding-width');

        // Phase 2: 视频展开
        expandTimer = setTimeout(() => {
          hotspot.classList.add('is-expanded');

          // GA4 埋点：Hotspot 展开
          if (window.Ergo360Analytics && productName) {
            window.Ergo360Analytics.hotspotExpand(productName, this.currentState);
          }

          if (video) {
            // 懒加载视频：首次 hover 时加载
            const source = video.querySelector('source[data-src]');
            if (source && !source.src) {
              source.src = source.dataset.src;
              video.load();
            }
            video.currentTime = 0;
            video.play().catch(() => {});
          }
        }, 200);
      });

      hotspot.addEventListener('mouseleave', () => {
        if (this.isMobile) return;

        if (expandTimer) {
          clearTimeout(expandTimer);
          expandTimer = null;
        }

        hotspot.classList.remove('is-expanded');
        if (video) {
          video.pause();
        }

        collapseTimer = setTimeout(() => {
          hotspot.classList.remove('is-expanding-width');
          if (content) {
            content.style.width = '';
          }
        }, 250);
      });
    });
  }

  /**
   * 设置初始状态
   */
  setInitialState() {
    let initialState = this.getInitialStateFromQuiz();
    if (!initialState) {
      const firstBtn = this.navBtns[0];
      initialState = firstBtn?.dataset.state;
    }

    if (initialState) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.switchState(initialState);
        });
      });
    }
  }

  /**
   * 初始化序列帧播放器
   */
  initFrameSequencePlayers() {
    const pcCanvases = this.container.querySelectorAll(
      '.ergo-360-product__lines:not(.ergo-360-product__lines--mobile) .ergo-360-product__line-canvas'
    );
    const mobileCanvases = this.container.querySelectorAll(
      '.ergo-360-product__lines--mobile .ergo-360-product__line-canvas'
    );

    const allCanvases = [...pcCanvases, ...mobileCanvases];

    allCanvases.forEach((canvas) => {
      const state = canvas.dataset.state;
      const baseUrl = canvas.dataset.frameBase;
      const frameName = canvas.dataset.frameName;
      const frameCount = parseInt(canvas.dataset.frameCount, 10);
      const fps = parseInt(canvas.dataset.frameFps, 10);

      if (!state || !baseUrl || !frameName || !frameCount || !fps) {
        return;
      }

      const player = new FrameSequencePlayer(canvas, {
        baseUrl,
        frameName,
        frameCount,
        fps
      });

      const isMobile = canvas.closest('.ergo-360-product__lines--mobile') !== null;
      const key = `${state}-${isMobile ? 'mobile' : 'pc'}`;
      this.frameSequencePlayers.set(key, player);
    });

    this.preloadAllFrameSequences();
  }

  /**
   * 预加载所有序列帧
   */
  async preloadAllFrameSequences() {
    const preloadPromises = [];

    this.frameSequencePlayers.forEach((player) => {
      preloadPromises.push(player.preload());
    });

    if (preloadPromises.length > 0) {
      try {
        await Promise.all(preloadPromises);
      } catch (error) {
        // 静默处理预加载失败
      }
    }
  }

  /**
   * 预加载指定状态的序列帧
   */
  async preloadFrameSequence(state) {
    const pcPlayer = this.frameSequencePlayers.get(`${state}-pc`);
    const mobilePlayer = this.frameSequencePlayers.get(`${state}-mobile`);

    const preloadPromises = [];
    if (pcPlayer) preloadPromises.push(pcPlayer.preload());
    if (mobilePlayer) preloadPromises.push(mobilePlayer.preload());

    if (preloadPromises.length > 0) {
      await Promise.all(preloadPromises);
    }
  }

  /**
   * 初始化 Quiz Result 功能
   */
  initQuizResult() {
    this.quizResultBtns = this.container.querySelectorAll('[data-quiz-result-btn]');
    this.quizPopup = this.container.querySelector('[data-quiz-popup]');
    this.quizPopupMask = this.quizPopup?.querySelector('.ergo-360-product__quiz-popup-mask');
    this.quizPopupCloseBtns = this.quizPopup?.querySelectorAll('.ergo-360-product__quiz-popup-close');
    this.quizRestartBtns = this.quizPopup?.querySelectorAll('[data-quiz-restart]');

    this.checkQuizLocalStorage();

    document.addEventListener('ergo360QuizComplete', (e) => {
      this.cachedQuizData = e.detail;
      this.showQuizResultBtns();

      const result = e.detail.result;
      const targetState = Ergo360Product.QUIZ_RESULT_TO_TAB[result];
      if (targetState && targetState !== this.currentState) {
        this.switchState(targetState);
      }
    });

    this.quizResultBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.openQuizPopup());
    });

    this.quizPopupMask?.addEventListener('click', () => this.closeQuizPopup());
    this.quizPopupCloseBtns?.forEach((btn) => {
      btn.addEventListener('click', () => this.closeQuizPopup());
    });

    this.quizRestartBtns?.forEach((btn) => {
      btn.addEventListener('click', () => this.restartQuiz());
    });
  }

  /**
   * 检查 localStorage 中是否有 Quiz 数据
   */
  checkQuizLocalStorage() {
    const savedData = localStorage.getItem('ergo360QuizData');
    if (!savedData) {
      this.hideQuizResultBtns();
      return;
    }

    try {
      const data = JSON.parse(savedData);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > sevenDays) {
        localStorage.removeItem('ergo360QuizData');
        this.hideQuizResultBtns();
        return;
      }

      if (data.result) {
        this.showQuizResultBtns();
        this.cachedQuizData = data;
      }
    } catch (e) {
      this.hideQuizResultBtns();
    }
  }

  /**
   * 从 localStorage 获取 Quiz 结果对应的 Tab 状态
   */
  getInitialStateFromQuiz() {
    const savedData = localStorage.getItem('ergo360QuizData');
    if (!savedData) return null;

    try {
      const data = JSON.parse(savedData);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > sevenDays) {
        return null;
      }

      const result = data.result;
      return Ergo360Product.QUIZ_RESULT_TO_TAB[result] || null;
    } catch (e) {
      return null;
    }
  }

  showQuizResultBtns() {
    this.quizResultBtns.forEach((btn) => {
      btn.classList.add('is-visible');
    });
  }

  hideQuizResultBtns() {
    this.quizResultBtns.forEach((btn) => {
      btn.classList.remove('is-visible');
    });
  }

  openQuizPopup() {
    if (!this.quizPopup || !this.cachedQuizData) return;

    const { result, resultData } = this.cachedQuizData;

    if (resultData) {
      const iconElements = this.quizPopup.querySelectorAll('[data-quiz-result-icon]');
      iconElements.forEach((el) => {
        el.src = resultData.icon || '';
        el.alt = resultData.label || result;
      });

      const labelElements = this.quizPopup.querySelectorAll('[data-quiz-result-label]');
      labelElements.forEach((el) => {
        el.textContent = resultData.label || result.toUpperCase();
      });

      const descElements = this.quizPopup.querySelectorAll('[data-quiz-result-description]');
      descElements.forEach((el) => {
        el.textContent = resultData.description || '';
      });
    }

    this.quizPopup.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeQuizPopup() {
    if (!this.quizPopup) return;
    this.quizPopup.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  restartQuiz() {
    localStorage.removeItem('ergo360QuizData');
    this.cachedQuizData = null;
    this.closeQuizPopup();
    this.hideQuizResultBtns();
  }

  /**
   * 初始化弹窗交互
   */
  initPopups() {
    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener('click', () => {
        const productName = hotspot
          .querySelector('.ergo-360-product__hotspot-name')
          ?.textContent.toLowerCase()
          .trim();
        if (productName) {
          if (this.isMobile) {
            this.openMobilePopup(productName);
          } else {
            this.openPopup(productName);
          }
        }
      });
    });

    this.popups.forEach((popup) => {
      const mask = popup.querySelector('.ergo-360-product__popup-mask');
      const closeBtn = popup.querySelector('.ergo-360-product__popup-close');
      const addToCartBtns = popup.querySelectorAll('.ergo-360-product__popup-btn');

      mask?.addEventListener('click', () => this.closePopup(popup));
      closeBtn?.addEventListener('click', () => this.closePopup(popup));

      addToCartBtns.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleAddToCart(e, popup));
      });

      const scrubControls = popup.querySelectorAll('.ergo-360-product__scrub-control');
      scrubControls.forEach(scrubControl => {
        this.initScrubControl(popup, scrubControl);
      });

      const segmentControls = popup.querySelectorAll('.ergo-360-product__segment-control');
      segmentControls.forEach(segmentControl => {
        this.initSegmentControl(popup, segmentControl);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllPopups();
      }
    });
  }

  /**
   * 初始化 scrub 控制器
   */
  initScrubControl(popup, scrubControl) {
    const track = scrubControl.querySelector('.ergo-360-product__scrub-track');
    const progress = scrubControl.querySelector('.ergo-360-product__scrub-progress');
    const thumb = scrubControl.querySelector('.ergo-360-product__scrub-thumb');

    const parent = scrubControl.parentElement;
    const mediaContainer = parent?.querySelector('.ergo-360-product__popup-video--scrub');

    const canvas = mediaContainer?.querySelector('.ergo-360-product__scrub-canvas');
    const video = mediaContainer?.querySelector('video');

    if (!track || !progress || !thumb || (!canvas && !video)) return;

    const useSequenceFrames = !!canvas;
    let scrubber = null;

    if (useSequenceFrames) {
      const baseUrl = canvas.dataset.baseUrl;
      const frameCount = parseInt(canvas.dataset.frameCount, 10) || 165;

      scrubber = new FrameSequenceScrubber(canvas, {
        baseUrl,
        frameCount
      });

      if (!popup._frameScrubbers) {
        popup._frameScrubbers = [];
      }
      popup._frameScrubbers.push(scrubber);
    } else {
      video.load();
    }

    let isDragging = false;
    let currentPercent = 50;
    const snapPoints = [0, 50, 100];

    const updatePosition = (percent, animate = false) => {
      percent = Math.max(0, Math.min(100, percent));
      currentPercent = percent;

      if (animate) {
        progress.style.transition = 'width 0.25s ease-out, left 0.25s ease-out';
        thumb.style.transition = 'left 0.25s ease-out';
      } else {
        progress.style.transition = 'none';
        thumb.style.transition = 'none';
      }

      if (percent <= 50) {
        progress.style.left = `${percent}%`;
        progress.style.width = `${50 - percent}%`;
        progress.style.borderRadius = '12px 0 0 12px';
      } else {
        progress.style.left = '50%';
        progress.style.width = `${percent - 50}%`;
        progress.style.borderRadius = '0 12px 12px 0';
      }

      thumb.style.left = `calc(${percent}% - 16px)`;

      if (useSequenceFrames && scrubber) {
        scrubber.showFrameByPercent(percent);
      } else if (video) {
        const duration = parseFloat(video.dataset.duration) || video.duration || 5.467;
        video.currentTime = (percent / 100) * duration;
      }
    };

    const findNearestSnapPoint = (percent) => {
      let nearest = snapPoints[0];
      let minDistance = Math.abs(percent - nearest);

      for (const point of snapPoints) {
        const distance = Math.abs(percent - point);
        if (distance < minDistance) {
          minDistance = distance;
          nearest = point;
        }
      }
      return nearest;
    };

    const getPercentFromEvent = (e) => {
      const rect = track.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return (x / rect.width) * 100;
    };

    track.addEventListener('click', (e) => {
      if (e.target === thumb || thumb.contains(e.target)) return;
      const clickPercent = getPercentFromEvent(e);
      const snapTo = findNearestSnapPoint(clickPercent);
      updatePosition(snapTo, true);
    });

    const startDrag = (e) => {
      isDragging = true;
      thumb.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const onDrag = (e) => {
      if (!isDragging) return;
      updatePosition(getPercentFromEvent(e), false);
    };

    const endDrag = () => {
      if (isDragging) {
        isDragging = false;
        thumb.style.cursor = 'grab';
        const snapTo = findNearestSnapPoint(currentPercent);
        updatePosition(snapTo, true);
      }
    };

    thumb.addEventListener('mousedown', startDrag);
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', endDrag);

    thumb.addEventListener('touchstart', startDrag, { passive: false });
    document.addEventListener('touchmove', onDrag, { passive: false });
    document.addEventListener('touchend', endDrag);

    popup._scrubUpdatePosition = updatePosition;
  }

  /**
   * 初始化视频分段控制器
   */
  initSegmentControl(popup, segmentControl) {
    const buttons = segmentControl.querySelectorAll('.ergo-360-product__segment-btn');

    const parentContainer = segmentControl.closest('.ergo-360-product__popup-left') ||
                           segmentControl.closest('.ergo-360-product__popup-right') ||
                           popup;
    const videoContainer = parentContainer.querySelector('.ergo-360-product__popup-video--segments');
    const video = videoContainer?.querySelector('video');

    if (!buttons.length || !video) return;

    video.load();

    const duration = parseFloat(video.dataset.duration) || video.duration || 9.333;

    const segments = [];

    buttons.forEach((btn, index) => {
      const start = parseFloat(btn.dataset.start);
      const end = parseFloat(btn.dataset.end);

      if (!isNaN(start) && !isNaN(end)) {
        segments.push({ start, end });
      } else {
        const segmentDuration = duration / buttons.length;
        segments.push({
          start: index * segmentDuration,
          end: (index + 1) * segmentDuration
        });
      }
    });

    let currentSegment = 1;
    let isSegmentActive = false;

    const onTimeUpdate = () => {
      if (!isSegmentActive) return;

      const segment = segments[currentSegment];
      if (!segment) return;

      const endTime = Math.min(segment.end, duration - 0.1);

      if (video.currentTime >= endTime) {
        video.currentTime = segment.start;
      }
    };

    const onEnded = () => {
      if (!isSegmentActive) return;

      const segment = segments[currentSegment];
      if (!segment) return;

      video.currentTime = segment.start;
      video.play().catch(() => {});
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);

    const playSegment = (segmentIndex) => {
      currentSegment = segmentIndex;
      isSegmentActive = true;

      const segment = segments[segmentIndex];
      if (!segment) return;

      video.currentTime = segment.start;
      video.play().catch(() => {});

      buttons.forEach((btn, index) => {
        btn.classList.toggle('is-active', index === segmentIndex);
      });
    };

    const stopSegmentLoop = () => {
      isSegmentActive = false;
      video.pause();
    };

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const segmentIndex = parseInt(btn.dataset.segment, 10);
        if (!isNaN(segmentIndex)) {
          playSegment(segmentIndex);
        }
      });
    });

    if (!popup._segmentPlaySegments) {
      popup._segmentPlaySegments = [];
      popup._segmentStopLoops = [];
    }
    popup._segmentPlaySegments.push(playSegment);
    popup._segmentStopLoops.push(stopSegmentLoop);

    popup._segmentPlaySegment = playSegment;
    popup._segmentStopLoop = stopSegmentLoop;
  }

  /**
   * 等待视频就绪
   */
  waitForVideoReady(video) {
    return new Promise((resolve) => {
      if (video.readyState >= 2) {
        resolve();
        return;
      }

      const onReady = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
        resolve();
      };

      const onError = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
        resolve();
      };

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('error', onError);

      setTimeout(() => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
        resolve();
      }, 5000);
    });
  }

  /**
   * 打开弹窗
   */
  async openPopup(productName) {
    const popup = [...this.popups].find((p) => p.dataset.popupTrigger === productName);
    if (popup) {
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // GA4 埋点：产品弹窗打开
      if (window.Ergo360Analytics) {
        window.Ergo360Analytics.popupOpen(productName);
      }

      const hasScrub = popup.querySelector('.ergo-360-product__scrub-control');
      const hasSegment = popup.querySelector('.ergo-360-product__segment-control');

      if (hasScrub && popup._scrubUpdatePosition) {
        if (popup._frameScrubbers && popup._frameScrubbers.length > 0) {
          await Promise.all(popup._frameScrubbers.map(s => s.preload()));

          const tryShowFrame = async (retries = 10, delay = 50) => {
            for (let i = 0; i < retries; i++) {
              await new Promise((resolve) => requestAnimationFrame(resolve));

              for (const scrubber of popup._frameScrubbers) {
                const rect = scrubber.canvas.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                  scrubber.setupCanvas();
                  scrubber.showFrameByPercent(50);
                  return;
                }
              }

              await new Promise((resolve) => setTimeout(resolve, delay));
            }
            const firstScrubber = popup._frameScrubbers[0];
            if (firstScrubber) {
              firstScrubber.setupCanvas();
              firstScrubber.showFrameByPercent(50);
            }
          };

          await tryShowFrame();
        } else {
          const scrubVideo = popup.querySelector('.ergo-360-product__popup-video--scrub video');
          if (scrubVideo) {
            await this.waitForVideoReady(scrubVideo);
          }
          popup._scrubUpdatePosition(50);
        }
      } else if (hasSegment && popup._segmentPlaySegments && popup._segmentPlaySegments.length > 0) {
        const segmentVideos = popup.querySelectorAll('.ergo-360-product__popup-video--segments video');
        for (const segmentVideo of segmentVideos) {
          // 懒加载视频
          this.lazyLoadVideo(segmentVideo);
          await this.waitForVideoReady(segmentVideo);
        }
        popup._segmentPlaySegments.forEach(playFn => playFn(1));
      } else {
        const video = popup.querySelector('video');
        if (video) {
          // 懒加载视频
          this.lazyLoadVideo(video);
          await this.waitForVideoReady(video);
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      }
    }
  }

  /**
   * 关闭弹窗
   */
  closePopup(popup) {
    popup.classList.remove('is-open');
    document.body.style.overflow = '';

    if (popup._segmentStopLoops && popup._segmentStopLoops.length > 0) {
      popup._segmentStopLoops.forEach(stopFn => stopFn());
    } else if (popup._segmentStopLoop) {
      popup._segmentStopLoop();
    }

    if (popup._scrubUpdatePosition) {
      popup._scrubUpdatePosition(50);
    }

    const video = popup.querySelector('video');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  /**
   * 关闭所有弹窗
   */
  closeAllPopups() {
    this.popups.forEach((popup) => {
      if (popup.classList.contains('is-open')) {
        this.closePopup(popup);
      }
    });
    this.mobilePopups.forEach((popup) => {
      if (popup.classList.contains('is-open')) {
        this.closeMobilePopup(popup);
      }
    });
    if (this.quizPopup?.classList.contains('is-open')) {
      this.closeQuizPopup();
    }
  }

  /**
   * 懒加载视频：将 data-src 转为 src
   */
  lazyLoadVideo(video) {
    if (!video) return;
    const source = video.querySelector('source[data-src]');
    if (source && !source.src) {
      source.src = source.dataset.src;
      video.load();
    }
  }

  /**
   * 初始化移动端弹窗
   */
  initMobilePopups() {
    this.mobilePopups.forEach((popup) => {
      const mask = popup.querySelector('.ergo-360-product__mobile-popup-mask');
      const closeBtn = popup.querySelector('.ergo-360-product__mobile-popup-close');
      const exploreBtn = popup.querySelector('.ergo-360-product__mobile-popup-explore');

      mask?.addEventListener('click', () => this.closeMobilePopup(popup));
      closeBtn?.addEventListener('click', () => this.closeMobilePopup(popup));

      exploreBtn?.addEventListener('click', () => {
        const targetProduct = exploreBtn.dataset.exploreTarget;
        this.closeMobilePopup(popup);
        setTimeout(() => {
          this.openPopup(targetProduct);
        }, 300);
      });
    });
  }

  /**
   * 打开移动端第一层弹窗
   */
  openMobilePopup(productName) {
    const popup = [...this.mobilePopups].find(
      (p) => p.dataset.mobilePopupTrigger === productName
    );
    if (popup) {
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      this.preloadFrameScrubber(productName);

      const video = popup.querySelector('video');
      if (video) {
        this.lazyLoadVideo(video);
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    }
  }

  /**
   * 关闭移动端第一层弹窗
   */
  closeMobilePopup(popup) {
    popup.classList.remove('is-open');
    document.body.style.overflow = '';

    const video = popup.querySelector('video');
    if (video) {
      video.pause();
    }
  }

  /**
   * 预加载指定产品的序列帧
   */
  preloadFrameScrubber(productName) {
    const popup = [...this.popups].find((p) => p.dataset.popupTrigger === productName);
    if (!popup) return;

    if (popup._frameScrubbers && popup._frameScrubbers.length > 0) {
      popup._frameScrubbers.forEach((scrubber) => {
        if (!scrubber.isLoaded && !scrubber.isLoading) {
          scrubber.preload();
        }
      });
    }
  }

  /**
   * 初始化入场动画（PC 端专用）
   */
  initEntranceAnimation() {
    if (this.isMobile) return;

    if (this.container.classList.contains('is-entrance-triggered')) {
      this.entranceTriggered = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            this.triggerEntranceAnimation();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(this.container);
  }

  /**
   * 触发首次入场动画
   */
  triggerEntranceAnimation() {
    if (this.entranceTriggered) return;
    this.entranceTriggered = true;

    this.container.classList.add('is-entrance-triggered');

    if (this.currentState) {
      setTimeout(() => {
        this.playLineAndHotspotAnimation(this.currentState);
      }, 700);
    }
  }

  /**
   * 播放线条和标签入场动画
   */
  playLineAndHotspotAnimation(state) {
    if (this.isMobile) {
      this.playMobileLineAnimation(state);
      return;
    }

    const hasLine = ['NECK', 'SPINE', 'LEGS', 'HIPS'].includes(state);

    if (hasLine) {
      const player = this.frameSequencePlayers.get(`${state}-pc`);

      if (player) {
        player.play();
        setTimeout(() => {
          this.animateHotspots();
        }, 2600);
      } else {
        const activeLine = this.container.querySelector(
          `.ergo-360-product__line:not(.ergo-360-product__line-canvas)[data-state="${state}"]`
        );
        if (activeLine) {
          activeLine.classList.remove('is-animating');
          void activeLine.offsetWidth;
          activeLine.classList.add('is-animating');
        }
        setTimeout(() => {
          this.animateHotspots();
        }, 1100);
      }
    } else {
      setTimeout(() => {
        this.animateHotspots();
      }, 600);
    }
  }

  /**
   * 移动端线条动画
   */
  playMobileLineAnimation(state) {
    const hasLine = ['NECK', 'SPINE', 'LEGS', 'HIPS'].includes(state);

    if (hasLine) {
      const player = this.frameSequencePlayers.get(`${state}-mobile`);
      if (player) {
        player.play();
      }
    }
  }

  /**
   * 逐个播放标签入场动画
   */
  animateHotspots() {
    const visibleHotspots = this.container.querySelectorAll(
      '.ergo-360-product__hotspot.is-visible'
    );
    visibleHotspots.forEach((hotspot, index) => {
      hotspot.classList.remove('is-animating');
      void hotspot.offsetWidth;
      setTimeout(() => {
        hotspot.classList.add('is-animating');
      }, index * 100);
    });
  }

  /**
   * 处理添加购物车
   */
  async handleAddToCart(e, popup) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;

    if (!variantId || btn.disabled || btn.classList.contains('is-loading')) return;

    // GA4 埋点：弹窗内点击"查看产品"（加购按钮）
    if (window.Ergo360Analytics) {
      const productName = popup.dataset.popupTrigger || '';
      window.Ergo360Analytics.popupViewProduct(productName);
    }

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }],
          sections: sectionsToBundle.length > 0 ? sectionsToBundle : undefined,
          sections_url: window.location.pathname
        }),
      });

      const result = await response.json();

      if (response.ok) {
        const cartResponse = await fetch('/cart.js', {
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        const cartJson = await cartResponse.json();

        if (result.sections) {
          cartJson['sections'] = result['sections'];
        }

        if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.publish) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'ergo-360-product',
            cart: cartJson
          });
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result }
        }));

        this.closePopup(popup);

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(btn);
        }
      } else {
        throw new Error(result.description || 'Add to cart failed');
      }
    } catch (error) {
      btn.classList.add('is-error');
      const btnText = btn.querySelector('span');
      if (btnText) btnText.textContent = 'ERROR';

      setTimeout(() => {
        btn.classList.remove('is-error');
        if (btnText) btnText.textContent = 'ADD TO CART';
      }, 2000);
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  /**
   * 切换状态
   */
  switchState(newState) {
    if (!newState) return;

    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }

    // GA4 埋点：身体部位切换
    if (window.Ergo360Analytics && this.currentState && this.currentState !== newState) {
      window.Ergo360Analytics.bodyPartSwitch(this.currentState, newState);
    }

    this.isAnimating = true;
    this.currentState = newState;

    // 更新导航按钮激活状态
    this.navBtns.forEach((btn) => {
      const isActive = btn.dataset.state === newState;
      btn.classList.toggle('is-active', isActive);
    });

    // 切换背景图
    this.backgrounds.forEach((bg) => {
      const isActive = bg.dataset.state === newState;
      bg.classList.toggle('is-active', isActive);
    });

    // 切换内容卡片
    this.cards.forEach((card) => {
      const isActive = card.dataset.state === newState;
      card.classList.toggle('is-active', isActive);
    });

    // 切换产品标签
    this.hotspots.forEach((hotspot) => {
      hotspot.classList.remove('is-animating');
    });

    const hasLine = ['NECK', 'SPINE', 'LEGS', 'HIPS'].includes(newState);
    const baseDelay = hasLine ? 1.1 : 0.6;
    const staggerDelay = 0.15;

    let visibleIndex = 0;
    this.hotspots.forEach((hotspot) => {
      const visibleStates = hotspot.dataset.states;
      if (visibleStates) {
        const statesArray = visibleStates.split(',').map((s) => s.trim());
        const isVisible = statesArray.includes(newState);

        if (isVisible) {
          const delay = baseDelay + visibleIndex * staggerDelay;
          hotspot.style.setProperty('--hotspot-delay', `${delay}s`);
          visibleIndex++;
        } else {
          hotspot.style.removeProperty('--hotspot-delay');
        }

        hotspot.classList.toggle('is-visible', isVisible);
      }
    });

    // 切换 line 装饰图层
    this.lines.forEach((line) => {
      const isActive = line.dataset.state === newState;
      line.classList.toggle('is-active', isActive);
      if (!isActive) {
        line.classList.remove('is-animating');
      }
    });

    // 重置非激活状态的序列帧播放器
    this.frameSequencePlayers.forEach((player, key) => {
      const [state] = key.split('-');
      if (state !== newState) {
        player.reset();
      }
    });

    // PC 端入场动画
    if (!this.isMobile && this.entranceTriggered) {
      this.playLineAndHotspotAnimation(newState);
    }

    // 移动端序列帧动画
    if (this.isMobile) {
      this.playMobileLineAnimation(newState);
    }

    // 动画完成后解锁
    const hasSequencePlayer = this.frameSequencePlayers.has(`${newState}-pc`) ||
                              this.frameSequencePlayers.has(`${newState}-mobile`);
    const unlockDelay = hasSequencePlayer ? 3600 : 2200;

    this.animationTimer = setTimeout(() => {
      this.isAnimating = false;
      this.animationTimer = null;
    }, unlockDelay);
  }
}

// 初始化
function initErgo360Product() {
  document.querySelectorAll('.ergo-360-product').forEach((container) => {
    if (!container.dataset.initialized) {
      new Ergo360Product(container);
      container.dataset.initialized = 'true';
    }
  });
}

// DOM Ready 时初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initErgo360Product);
} else {
  initErgo360Product();
}

// 支持 Shopify Section Rendering API
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.ergo-360-product')) {
    initErgo360Product();
  }
});
